import { useState } from "react";

/*
 * Echte Aufnahmen aus der HandVero-Software (PC) und der Handy-App. Die Bilder entstehen mit
 * `pnpm screenshots` aus der laufenden Software mit einer Vorführ-Firma und liegen in public/screens.
 */

const PC_W = 2160;
const PC_H = 1350;
const PH_W = 1170;
const PH_H = 2532;

function PcShot({ name, alt, eager }: { name: string; alt: string; eager?: boolean }) {
  return (
    <figure className="sx-pc">
      <div className="sx-bar" aria-hidden="true">
        <span className="sx-dots">
          <i />
          <i />
          <i />
        </span>
        <span>HandVero</span>
      </div>
      <img src={`/screens/${name}.jpg`} alt={alt} width={PC_W} height={PC_H} loading={eager ? "eager" : "lazy"} decoding="async" />
    </figure>
  );
}

function PhoneShot({ name, alt, eager }: { name: string; alt: string; eager?: boolean }) {
  return (
    <figure className="sx-phone">
      <img src={`/screens/${name}.jpg`} alt={alt} width={PH_W} height={PH_H} loading={eager ? "eager" : "lazy"} decoding="async" />
    </figure>
  );
}

/** Startseite oben: PC-Software mit Handy davor. */
export function HeroShots() {
  return (
    <div className="sx-hero">
      <PcShot name="pc-fotos" alt="HandVero am PC: Projekt Wohnbau Schneider, Ordner Verteilungen mit Fotos" eager />
      <PhoneShot name="handy-fotos" alt="HandVero auf dem Handy: dieselben Fotos im Ordner Verteilungen, unten der Kamera-Knopf" eager />
    </div>
  );
}

const TOUR: { id: string; label: string; title: string; text: string; pc: [string, string]; phone?: [string, string]; paper?: [string, string]; addon?: boolean }[] = [
  {
    id: "projekte",
    label: "Projekte",
    title: "Jedes Projekt mit seiner Ordnerstruktur",
    text: "Links die Projekte, rechts die Ordner. Nummeriert, umbenennbar, als Vorlage speicherbar. Auf dem Handy dieselbe Struktur, groß zum Antippen.",
    pc: ["pc-projekt", "Projekt Wohnbau Schneider mit nummerierten Ordnern am PC"],
    phone: ["handy-projekte", "Projektliste auf dem Handy"],
  },
  {
    id: "fotos",
    label: "Fotos",
    title: "Foto auf der Baustelle, im Büro schon da",
    text: "Ordner auf dem Handy wählen und auslösen. Das Foto liegt sofort im richtigen Ordner und ist am PC zu sehen, ohne Kabel und ohne Mail.",
    pc: ["pc-fotos", "Fotos im Ordner Verteilungen am PC"],
    phone: ["handy-fotos", "Fotos im Ordner Verteilungen auf dem Handy"],
  },
  {
    id: "notizen",
    label: "Notizen",
    title: "Absprachen festhalten, gesprochen oder geschrieben",
    text: "Notizen und Sprachmemos hängen am Projekt. Was mit dem Kunden besprochen wurde, steht im Projekt und nicht nur im Kopf.",
    pc: ["pc-notizen", "Notizen mit Projektzuordnung am PC"],
    phone: ["handy-notizen", "Notizen auf dem Handy"],
  },
  {
    id: "kalender",
    label: "Kalender",
    title: "Termine fürs Team, auf Wunsch mit Outlook",
    text: "Termine mit Projekt, Ort und zuständiger Person. Jeder sieht seine Einsätze, und wer möchte, verbindet seinen Outlook-Kalender.",
    pc: ["pc-kalender", "Kalender mit Terminen am PC"],
    phone: ["handy-kalender", "Kalender auf dem Handy"],
  },
  {
    id: "vero",
    label: "Vero",
    title: "Vero sagt, wohin damit",
    text: "Einfach sagen oder schreiben, was ansteht. Vero schlägt das passende Projekt vor, findet Fotos, Dateien und Termine und erklärt jede Funktion. Ohne fremden KI-Dienst.",
    pc: ["pc-vero", "Vero schlägt am PC das Projekt Wohnbau Schneider für eine Notiz vor"],
    phone: ["handy-vero", "Vero erklärt auf dem Handy, wie man das Konto löscht"],
  },
  {
    id: "rechnungen",
    label: "Rechnungen",
    title: "Vom Angebot zur Rechnung in einem Fluss",
    text: "Angebot, Auftrag und Rechnung bauen aufeinander auf. Nummern laufen lückenlos, fertige Rechnungen sind gesperrt, Zahlungsziel und Texte wählen Sie aus Vorlagen. Auf Wunsch mit Ihrem Logo, Ihrer Farbe und einem Zahl-QR-Code.",
    pc: ["pc-rechnungen", "Rechnungsliste mit offenen und fälligen Beträgen am PC"],
    paper: ["pdf-rechnung", "Beispiel einer Rechnung als PDF mit Zahl-QR-Code"],
    addon: true,
  },
  {
    id: "uebersicht",
    label: "Übersicht",
    title: "Einnahmen, Ausgaben und was noch offen ist",
    text: "Auf einen Blick, was bezahlt, offen oder überfällig ist. Mahnungen entstehen aus der Rechnung, die Zahlen gehen als Export an Ihre Steuerberatung.",
    pc: ["pc-uebersicht", "Geschäftsübersicht mit Einnahmen und Ausgaben am PC"],
    addon: true,
  },
  {
    id: "ausgaben",
    label: "Ausgaben",
    title: "Belege sammeln, ohne Zettelwirtschaft",
    text: "Eingangsrechnungen und Quittungen erfassen, Beleg als Foto oder PDF anhängen. Gebuchte Ausgaben sind gesperrt.",
    pc: ["pc-ausgaben", "Ausgabenliste mit Belegen am PC"],
    addon: true,
  },
  {
    id: "bank",
    label: "Bank",
    title: "Kontoauszug einlesen, Zahlungen zuordnen",
    text: "Kontoumsätze aus Ihrer Bank-Datei einlesen. HandVero ordnet Zahlungen den offenen Rechnungen zu, Sie bestätigen nur noch.",
    pc: ["pc-bank", "Bankumsätze mit zugeordneten Rechnungen am PC"],
    addon: true,
  },
];

/** Rundgang durch die Software in Reitern: PC und Handy nebeneinander. */
export function ScreenTour() {
  const [active, setActive] = useState(TOUR[0]!.id);
  const item = TOUR.find((t) => t.id === active) ?? TOUR[0]!;
  return (
    <div className="sx-tour">
      <div className="tabs" role="tablist" aria-label="Rundgang durch HandVero">
        {TOUR.map((t) => (
          <button key={t.id} role="tab" id={`sx-tab-${t.id}`} aria-selected={t.id === active} aria-controls="sx-panel" onClick={() => setActive(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="sx-panel" id="sx-panel" role="tabpanel" aria-labelledby={`sx-tab-${item.id}`} key={item.id}>
        <div className="sx-copy">
          <h3>{item.title}</h3>
          <p>{item.text}</p>
          <span className="sx-real">Echte Aufnahme aus HandVero</span>
          {item.addon && <span className="sx-addon">In den bezahlten Paketen enthalten</span>}
        </div>
        <div className="sx-duo">
          <PcShot name={item.pc[0]} alt={item.pc[1]} />
          {item.phone && <PhoneShot name={item.phone[0]} alt={item.phone[1]} />}
          {item.paper && (
            <figure className="sx-paper">
              <img src={`/screens/${item.paper[0]}.jpg`} alt={item.paper[1]} width={910} height={1287} loading="lazy" decoding="async" />
            </figure>
          )}
        </div>
      </div>
    </div>
  );
}

/** Drei Handy-Aufnahmen nebeneinander, für die Seite zur Web-App. */
export function PhoneRow() {
  const shots: [string, string, string][] = [
    ["handy-projekte", "Projekte", "Projektliste auf dem Handy"],
    ["handy-fotos", "Fotos im Ordner", "Fotos im Ordner Verteilungen auf dem Handy"],
    ["handy-kalender", "Termine", "Kalender auf dem Handy"],
  ];
  return (
    <div className="sx-row">
      {shots.map(([name, label, alt]) => (
        <div key={name} className="sx-row-item">
          <PhoneShot name={name} alt={alt} />
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}
